/**
 * Deterministic mirror of the analyzer's SVG transform.
 *
 * Mirrors `_compute_transform` in `packages/analyzer/src/dxf_analyzer/svg.py`:
 * geometry is centred inside an 800×600 viewBox with 24px padding, scaled
 * to fit (preserving aspect ratio), Y-flipped (CAD up-positive → SVG
 * down-positive). Knowing this exact transform lets the FE position
 * overlay annotations (dimensions, callouts, snap targets) in SVG
 * user-space without ever fetching or parsing the rendered SVG.
 *
 * Keep these constants in sync with svg.py — if either side moves,
 * overlays will drift.
 */

export const SVG_VIEW_W = 800;
export const SVG_VIEW_H = 600;
export const SVG_PADDING = 24;

export interface SvgGeometryRect {
  /** SVG user-space x of geometry top-left. */
  x: number;
  /** SVG user-space y of geometry top-left. */
  y: number;
  /** Geometry width in SVG user-space. */
  w: number;
  /** Geometry height in SVG user-space. */
  h: number;
  /** mm → SVG user-space scale factor. */
  scale: number;
}

/**
 * Returns where the part sits inside the SVG viewBox, given its mm
 * dimensions. Returns `null` for non-positive inputs.
 */
export function geometryRectInSvg(
  widthMm: number,
  heightMm: number,
): SvgGeometryRect | null {
  if (widthMm <= 0 || heightMm <= 0) return null;
  const innerW = SVG_VIEW_W - 2 * SVG_PADDING;
  const innerH = SVG_VIEW_H - 2 * SVG_PADDING;
  const scale = Math.min(innerW / widthMm, innerH / heightMm);
  const drawnW = widthMm * scale;
  const drawnH = heightMm * scale;
  const x = SVG_PADDING + (innerW - drawnW) / 2;
  const y = SVG_PADDING + (innerH - drawnH) / 2;
  return { x, y, w: drawnW, h: drawnH, scale };
}
